import React from 'react';
import {
  ChevronLeft, ShieldCheck, CheckCircle2, Wrench, FileText, Wallet, MapPin,
  Camera, Lock, AlertTriangle, Clock, X, Loader2, Info, UserCircle
} from 'lucide-react';
import { useAppContext } from '../context/AppContext';
import { v2, normalizeString, normalizeServiceKey, getServiceName, getWorkerReviewStatus, PANAMA_ZONES } from '../constants';

export default function EditProfileView() {
  const {
    lang, t, user, userData, role, services,
    editData, setEditData, uploading, setPreviewImage,
    setView, handleSaveProfile, compressImage
  } = useAppContext();

  const isVerifiedWorker = role === 'worker' && userData?.isVerified;
  const workerReviewStatus = role === 'worker' ? getWorkerReviewStatus(userData || {}) : null;

  const editIdentityComplete = Boolean(editData.firstName?.trim() && editData.lastName?.trim() && editData.phone?.trim() && editData.address?.trim());
  const editSkillsComplete = Boolean(editData.skills && editData.skills.length > 0);
  const editInvoiceTermsComplete = Boolean(editData.invoiceTermsAccepted);
  const editExpectedBankName = normalizeString(`${editData.firstName} ${editData.lastName}`);
  const editEnteredBankName = normalizeString(editData.accName || '');
  const editBankNameMatches = !editEnteredBankName || editExpectedBankName === editEnteredBankName;
  const editBankComplete = Boolean(editData.accName?.trim() && editData.accNum?.trim() && editData.accType && editBankNameMatches);
  const editDocsComplete = Boolean(editData.idPhoto && editData.selfie);

  const workerEditChecklist = [
    { id: 'identity', label: lang === 'es' ? 'Datos' : '身份资料', done: isVerifiedWorker || editIdentityComplete, icon: UserCircle },
    { id: 'skills', label: lang === 'es' ? 'Servicios' : '服务类型', done: isVerifiedWorker || editSkillsComplete, icon: Wrench },
    { id: 'invoice', label: lang === 'es' ? 'Factura' : '发票规则', done: isVerifiedWorker || editInvoiceTermsComplete, icon: FileText },
    { id: 'bank', label: lang === 'es' ? 'Cobro' : '收款账户', done: isVerifiedWorker || editBankComplete, icon: Wallet },
    { id: 'docs', label: lang === 'es' ? 'Fotos' : '证件照片', done: isVerifiedWorker || editDocsComplete, icon: Camera }
  ];
  const workerEditCompleted = workerEditChecklist.filter(item => item.done).length;
  const workerEditReady = role !== 'worker' || isVerifiedWorker || workerEditCompleted === workerEditChecklist.length;

  const fieldStyle = (disabled = false) => ({
    ...v2.field,
    backgroundColor: disabled ? '#F3F4F6' : '#fff',
    color: disabled ? '#64748B' : v2.colors.ink
  });

  const renderPhotoBox = (field, label) => {
    const value = editData[field];
    return (
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ fontSize: '11px', color: v2.colors.muted, margin: '0 0 7px 0', fontWeight: '900' }}>{label}</p>
        <div style={{ height: '112px' }}>
          {value ? (
            <div style={{ position: 'relative', width: '100%', height: '100%' }}>
              <img src={value} onClick={() => setPreviewImage(value)}
                style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '8px', cursor: 'pointer', opacity: isVerifiedWorker ? 0.72 : 1, border: '1px solid #D8DEE8' }} alt={label} />
              {!isVerifiedWorker && (
                <button onClick={() => setEditData({ ...editData, [field]: null })}
                  style={{ position: 'absolute', top: 6, right: 6, width: '26px', height: '26px', border: 'none', background: 'rgba(15,23,42,0.72)', color: '#fff', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <X size={15} />
                </button>
              )}
            </div>
          ) : (
            <label style={{ width: '100%', height: '100%', border: '1px dashed #94A3B8', borderRadius: '8px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: isVerifiedWorker ? 'not-allowed' : 'pointer', background: '#F8FAFC', boxSizing: 'border-box', color: v2.colors.ink, opacity: isVerifiedWorker ? 0.58 : 1 }}>
              <Camera size={22} color="#2563EB" />
              <span style={{ fontSize: '11px', marginTop: '8px', fontWeight: '900', textAlign: 'center', lineHeight: '1.25' }}>{label}</span>
              {!isVerifiedWorker && (
                <input type="file" accept="image/*" style={{ display: 'none' }}
                  onChange={e => compressImage(e.target.files[0], d => setEditData({ ...editData, [field]: d }))} />
              )}
            </label>
          )}
        </div>
      </div>
    );
  };

  return (
    <div style={{ padding: '14px 16px 24px' }}>
      {/* 页头 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
        <button onClick={() => setView('profile')} style={{ width: '38px', height: '38px', borderRadius: '8px', border: '1px solid #D8DEE8', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
          <ChevronLeft size={22} />
        </button>
        <div>
          <p style={{ margin: 0, color: v2.colors.muted, fontSize: '12px', fontWeight: '700' }}>
            {role === 'worker' ? (lang === 'es' ? 'Revisión de técnico' : '师傅审核资料') : (lang === 'es' ? 'Datos de cuenta' : '账户资料')}
          </p>
          <h2 style={{ fontWeight: '900', margin: 0, color: v2.colors.ink, fontSize: '21px' }}>{t.edit_profile}</h2>
        </div>
      </div>

      {/* 师傅：资料完成进度 */}
      {role === 'worker' && (
        <div style={{ ...v2.card, padding: '16px', marginBottom: '14px', border: workerEditReady ? '1px solid #BBF7D0' : v2.card.border }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', marginBottom: '12px' }}>
            <div>
              <p style={{ margin: '0 0 4px 0', color: v2.colors.muted, fontSize: '12px', fontWeight: '700' }}>
                {lang === 'es' ? 'Progreso del perfil' : '资料完成度'}
              </p>
              <h3 style={{ margin: 0, color: workerEditReady ? v2.colors.green : v2.colors.ink, fontSize: '18px' }}>
                {isVerifiedWorker
                  ? (lang === 'es' ? 'Perfil verificado' : '资料已认证')
                  : workerEditReady
                    ? (lang === 'es' ? 'Listo para revisión' : '可以提交审核')
                    : (lang === 'es' ? `${workerEditCompleted}/${workerEditChecklist.length} completado` : `已完成 ${workerEditCompleted}/${workerEditChecklist.length}`)}
              </h3>
            </div>
            <span style={{ background: workerEditReady ? v2.colors.softGreen : '#FFF7E6', color: workerEditReady ? '#15803D' : '#B45309', border: `1px solid ${workerEditReady ? '#BBF7D0' : '#FCD34D'}`, borderRadius: '8px', padding: '6px 8px', fontSize: '11px', fontWeight: '900', whiteSpace: 'nowrap' }}>
              {isVerifiedWorker ? t.verified : workerEditReady ? (lang === 'es' ? 'Completo' : '已完整') : (lang === 'es' ? 'Pendiente' : '待完善')}
            </span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(66px, 1fr))', gap: '7px' }}>
            {workerEditChecklist.map(item => {
              const Icon = item.icon;
              return (
                <div key={item.id} style={{ minHeight: '66px', background: item.done ? v2.colors.softGreen : '#F8FAFC', border: `1px solid ${item.done ? '#BBF7D0' : '#E3E8EF'}`, borderRadius: '8px', padding: '8px 5px', textAlign: 'center', boxSizing: 'border-box' }}>
                  {item.done
                    ? <CheckCircle2 size={16} color={v2.colors.green} style={{ margin: '0 auto 5px' }} />
                    : <Icon size={16} color="#94A3B8" style={{ margin: '0 auto 5px' }} />}
                  <p style={{ margin: 0, fontSize: '10px', color: item.done ? '#166534' : v2.colors.muted, fontWeight: '900', lineHeight: '1.15' }}>{item.label}</p>
                </div>
              );
            })}
          </div>
          {isVerifiedWorker && (
            <div style={{ background: '#F8FAFC', border: '1px solid #E3E8EF', padding: '12px', borderRadius: '8px', marginTop: '12px', display: 'flex', gap: '8px' }}>
              <Lock size={17} color={v2.colors.green} style={{ flexShrink: 0 }} />
              <p style={{ margin: 0, fontSize: '12px', color: '#475569', lineHeight: '1.45', fontWeight: '700' }}>
                {lang === 'es' ? 'Tus datos principales y bancarios están bloqueados por seguridad. Contacta al soporte para modificarlos.' : '核心身份与收款资料已锁定保护。如需修改，请联系客服处理。'}
              </p>
            </div>
          )}
          {workerReviewStatus === 'REJECTED' && !isVerifiedWorker && (
            <div style={{ background: v2.colors.softRed, border: '1px solid #FECACA', padding: '12px', borderRadius: '8px', marginTop: '12px', display: 'flex', gap: '8px' }}>
              <AlertTriangle size={17} color="#B91C1C" style={{ flexShrink: 0 }} />
              <div>
                <p style={{ margin: 0, fontSize: '12px', color: '#7F1D1D', lineHeight: '1.45', fontWeight: '800' }}>
                  {lang === 'es' ? 'Motivo del rechazo:' : '驳回原因：'} {userData?.verification_rejection || (lang === 'es' ? 'Revisa tus datos y vuelve a guardar.' : '请检查资料后重新保存提交。')}
                </p>
                <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#7F1D1D', lineHeight: '1.45', fontWeight: '800' }}>
                  {lang === 'es' ? 'Después de corregir, toca el botón de abajo para guardar y reenviar a revisión.' : '修改完成后，请点击底部按钮保存并重新提交审核。'}
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── 头像上传 ── */}
      <div style={{ ...v2.card, padding: '20px 16px', marginBottom: '14px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <div style={{ position: 'relative', marginBottom: '10px' }}>
          {editData.avatar ? (
            <img src={editData.avatar}
              style={{ width: '88px', height: '88px', borderRadius: '50%', objectFit: 'cover', border: '3px solid #E8ECF0', display: 'block' }}
              alt="avatar" />
          ) : (
            <div style={{ width: '88px', height: '88px', borderRadius: '50%', background: 'linear-gradient(135deg, #1648D8, #2962FF)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <UserCircle size={48} color="#fff" />
            </div>
          )}
          {/* 编辑相机按钮 */}
          <label style={{ position: 'absolute', bottom: '2px', right: '2px', width: '28px', height: '28px', borderRadius: '50%', background: '#2962FF', border: '2px solid #fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 2px 6px rgba(41,98,255,0.35)' }}>
            <Camera size={14} color="#fff" />
            <input type="file" accept="image/*" style={{ display: 'none' }}
              onChange={e => compressImage(e.target.files[0], d => setEditData({ ...editData, avatar: d }))} />
          </label>
        </div>
        <span style={{ fontSize: '12px', color: v2.colors.muted, fontWeight: '700' }}>
          {lang === 'es' ? 'Toca el ícono para cambiar foto' : '点击相机图标更换头像'}
        </span>
        {role === 'worker' && (
          <div style={{ marginTop: '10px', background: '#EBF1FF', border: '1px solid #BFDBFE', borderRadius: '10px', padding: '9px 12px', display: 'flex', gap: '7px', alignItems: 'flex-start', maxWidth: '320px', width: '100%' }}>
            <ShieldCheck size={14} color="#2962FF" style={{ flexShrink: 0, marginTop: '1px' }} />
            <span style={{ fontSize: '11px', color: '#1D4ED8', fontWeight: '700', lineHeight: '1.4' }}>
              {lang === 'es'
                ? 'Esta foto se mostrará al cliente cuando aceptes su pedido, para que pueda verificar tu identidad.'
                : '接单后此头像会展示给客户，用于上门身份核验。请使用清晰的正脸照片。'}
            </span>
          </div>
        )}
      </div>

      {/* 基本资料 */}
      <div style={{ ...v2.card, padding: '16px', marginBottom: '14px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', marginBottom: '14px' }}>
          <div>
            <p style={{ margin: '0 0 4px 0', color: v2.colors.muted, fontSize: '12px', fontWeight: '700' }}>{t.phone} (ID)</p>
            <b style={{ fontSize: '16px', color: v2.colors.ink }}>
              {user?.email?.includes('@fixbox.local') ? `+507 ${user.email.split('@')[0]}` : user?.email}
            </b>
          </div>
          <ShieldCheck size={20} color={role === 'worker' && !isVerifiedWorker ? '#B45309' : v2.colors.green} />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '12px' }}>
          <div>
            <p style={{ fontSize: '12px', color: v2.colors.muted, margin: '0 0 7px 0', fontWeight: '800' }}>{t.lbl_fn}</p>
            <input disabled={isVerifiedWorker} value={editData.firstName || ''} onChange={e => setEditData({ ...editData, firstName: e.target.value })} style={fieldStyle(isVerifiedWorker)} />
          </div>
          <div>
            <p style={{ fontSize: '12px', color: v2.colors.muted, margin: '0 0 7px 0', fontWeight: '800' }}>{t.lbl_ln}</p>
            <input disabled={isVerifiedWorker} value={editData.lastName || ''} onChange={e => setEditData({ ...editData, lastName: e.target.value })} style={fieldStyle(isVerifiedWorker)} />
          </div>
        </div>

        <p style={{ fontSize: '12px', color: v2.colors.muted, margin: '0 0 7px 0', fontWeight: '800' }}>{t.phone}</p>
        <input value={editData.phone || ''} placeholder={lang === 'es' ? 'Ej: 50760000000' : '例如: 50760000000'} onChange={e => setEditData({ ...editData, phone: e.target.value })} style={{ ...v2.field, marginBottom: '12px' }} />

        <p style={{ fontSize: '12px', color: v2.colors.muted, margin: '0 0 7px 0', fontWeight: '800' }}>{t.address}</p>
        <textarea value={editData.address || ''} placeholder={t.addr_placeholder} onChange={e => setEditData({ ...editData, address: e.target.value })} style={{ ...v2.field, height: '82px', resize: 'vertical' }} />
      </div>

      {/* 师傅：服务类型 */}
      {role === 'worker' && (
        <div style={{ ...v2.card, padding: '16px', marginBottom: '14px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', marginBottom: '12px' }}>
            <div>
              <p style={{ fontSize: '12px', color: v2.colors.muted, margin: '0 0 4px 0', fontWeight: '800' }}>{t.skills_label}</p>
              <h3 style={{ margin: 0, color: v2.colors.ink, fontSize: '17px' }}>{t.select_skills}</h3>
            </div>
            <span style={{ fontSize: '12px', color: editSkillsComplete ? v2.colors.green : v2.colors.red, fontWeight: '900' }}>{editData.skills?.length || 0}</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            {services.filter(s => s.id !== 'S-999').map(s => {
              const serviceKey = normalizeServiceKey(s.service_name);
              const checked = (editData.skills || []).some(name => normalizeServiceKey(name) === serviceKey);
              return (
                <label key={s.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', background: checked ? v2.colors.softBlue : '#F8FAFC', padding: '10px', borderRadius: '8px', border: `1px solid ${checked ? '#BFDBFE' : '#E3E8EF'}`, cursor: isVerifiedWorker ? 'not-allowed' : 'pointer', opacity: isVerifiedWorker ? 0.68 : 1, minHeight: '46px', boxSizing: 'border-box' }}>
                  <input disabled={isVerifiedWorker} type="checkbox" checked={checked}
                    onChange={e => {
                      if (e.target.checked) {
                        const nextSkills = [...(editData.skills || []).filter(name => normalizeServiceKey(name) !== serviceKey), s.service_name.trim()];
                        setEditData({ ...editData, skills: nextSkills });
                      } else {
                        setEditData({ ...editData, skills: (editData.skills || []).filter(name => normalizeServiceKey(name) !== serviceKey) });
                      }
                    }}
                    style={{ accentColor: '#2563EB', flexShrink: 0 }} />
                  <span style={{ lineHeight: '1.2', color: v2.colors.ink, fontWeight: '800' }}>{getServiceName(s.service_name, lang)}</span>
                </label>
              );
            })}
          </div>
        </div>
      )}

      {/* 师傅：服务覆盖区域 */}
      {role === 'worker' && (
        <div style={{ ...v2.card, padding: '16px', marginBottom: '14px' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '12px' }}>
            <div style={{ width: '38px', height: '38px', borderRadius: '8px', background: '#EBF5FF', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <MapPin size={20} color="#2962FF" />
            </div>
            <div>
              <p style={{ fontSize: '12px', color: v2.colors.muted, margin: '0 0 4px 0', fontWeight: '800' }}>{lang === 'es' ? 'Cobertura' : '服务范围'}</p>
              <h3 style={{ margin: 0, color: v2.colors.ink, fontSize: '17px' }}>{t.coverage_label}</h3>
              <p style={{ margin: '5px 0 0 0', fontSize: '11px', color: '#4B5563', lineHeight: '1.4', fontWeight: '700' }}>{t.coverage_desc}</p>
            </div>
          </div>
          {/* 已开放区域 */}
          <p style={{ margin: '0 0 6px 0', fontSize: '11px', fontWeight: '900', color: v2.colors.green, letterSpacing: '0.5px' }}>
            {lang === 'es' ? '✅ ZONAS ACTIVAS' : '✅ 已开放区域'}
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '14px' }}>
            {PANAMA_ZONES.filter(z => z.active !== false).map(z => {
              const selected = (editData.coverageZones || []).includes(z.id);
              return (
                <button key={z.id} type="button"
                  onClick={() => {
                    const current = editData.coverageZones || [];
                    const next = selected ? current.filter(id => id !== z.id) : [...current, z.id];
                    setEditData({ ...editData, coverageZones: next });
                  }}
                  style={{ padding: '7px 12px', borderRadius: '20px', border: `1px solid ${selected ? '#2962FF' : '#D8DEE8'}`, background: selected ? '#EBF5FF' : '#fff', color: selected ? '#2962FF' : v2.colors.ink, fontWeight: '700', fontSize: '13px', cursor: 'pointer' }}>
                  {z.label}
                </button>
              );
            })}
          </div>
          {/* 即将开放区域 */}
          <p style={{ margin: '0 0 6px 0', fontSize: '11px', fontWeight: '900', color: v2.colors.muted, letterSpacing: '0.5px' }}>
            {lang === 'es' ? '🔜 PRÓXIMAMENTE — Selecciona para reservar tu lugar' : '🔜 即将开放 — 提前勾选占位'}
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {PANAMA_ZONES.filter(z => z.active === false).map(z => {
              const selected = (editData.coverageZones || []).includes(z.id);
              return (
                <button key={z.id} type="button"
                  onClick={() => {
                    const current = editData.coverageZones || [];
                    const next = selected ? current.filter(id => id !== z.id) : [...current, z.id];
                    setEditData({ ...editData, coverageZones: next });
                  }}
                  style={{ padding: '7px 12px', borderRadius: '20px', border: `1px solid ${selected ? '#9CA3AF' : '#E8EAED'}`, background: selected ? '#F1F5F9' : '#F8FAFC', color: selected ? '#4B5563' : '#9CA3AF', fontWeight: '700', fontSize: '13px', cursor: 'pointer' }}>
                  {z.label}
                </button>
              );
            })}
          </div>
          {(editData.coverageZones || []).length === 0 && (
            <p style={{ margin: '10px 0 0 0', fontSize: '12px', color: '#94A3B8', fontWeight: '700' }}>
              {lang === 'es' ? 'Sin zona seleccionada — recibirás pedidos de toda la ciudad.' : '未选区域 — 默认接收全市订单。'}
            </p>
          )}
        </div>
      )}

      {/* 师傅：发票规则 */}
      {role === 'worker' && (
        <div style={{ ...v2.card, padding: '16px', marginBottom: '14px', border: editInvoiceTermsComplete ? v2.card.border : '1px solid #FCD34D' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '12px' }}>
            <div style={{ width: '38px', height: '38px', borderRadius: '8px', background: v2.colors.softGold, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <FileText size={20} color={v2.colors.gold} />
            </div>
            <div>
              <p style={{ fontSize: '12px', color: v2.colors.muted, margin: '0 0 4px 0', fontWeight: '800' }}>{lang === 'es' ? 'Factura fiscal' : '财政发票'}</p>
              <h3 style={{ margin: 0, color: v2.colors.ink, fontSize: '17px' }}>{lang === 'es' ? 'Regla para técnicos' : '师傅开票规则'}</h3>
              <p style={{ margin: '6px 0 0 0', fontSize: '11px', color: '#854D0E', lineHeight: '1.45', fontWeight: '700' }}>
                {lang === 'es'
                  ? 'FixBox solo emite recibo de servicio. Puedes trabajar aunque no emitas factura; revisa cada pedido y solo acepta los que pidan factura si puedes cumplir.'
                  : 'FixBox 只提供服务凭证。不会开发票也可以接普通单；请看清每个订单要求，只有能处理时才接需要发票的订单。'}
              </p>
            </div>
          </div>
          <label style={{ display: 'flex', gap: '9px', alignItems: 'flex-start', background: editData.invoiceTermsAccepted ? v2.colors.softGreen : '#FFF7E6', border: `1px solid ${editData.invoiceTermsAccepted ? '#BBF7D0' : '#FCD34D'}`, borderRadius: '8px', padding: '10px', marginBottom: '10px', cursor: 'pointer' }}>
            <input type="checkbox" checked={Boolean(editData.invoiceTermsAccepted)} onChange={e => setEditData({ ...editData, invoiceTermsAccepted: e.target.checked })} style={{ marginTop: '2px', accentColor: v2.colors.green, flexShrink: 0 }} />
            <span style={{ fontSize: '12px', color: editData.invoiceTermsAccepted ? '#166534' : '#854D0E', lineHeight: '1.4', fontWeight: '800' }}>
              {lang === 'es' ? 'Entiendo y acepto esta regla de factura.' : '我已了解并同意此开票规则。'}
            </span>
          </label>
          <label style={{ display: 'flex', gap: '9px', alignItems: 'flex-start', background: '#F8FAFC', border: '1px solid #E3E8EF', borderRadius: '8px', padding: '10px', marginBottom: editData.canIssueInvoice ? '10px' : 0, cursor: 'pointer' }}>
            <input type="checkbox" checked={Boolean(editData.canIssueInvoice)} onChange={e => setEditData({ ...editData, canIssueInvoice: e.target.checked })} style={{ marginTop: '2px', accentColor: '#2563EB', flexShrink: 0 }} />
            <span style={{ fontSize: '12px', color: v2.colors.ink, lineHeight: '1.4', fontWeight: '800' }}>
              {lang === 'es' ? 'Puedo emitir factura fiscal cuando el cliente la solicite.' : '客户需要正式发票时，我可以处理。'}
            </span>
          </label>
          {editData.canIssueInvoice && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              <div style={{ gridColumn: '1 / -1' }}>
                <p style={{ fontSize: '12px', color: v2.colors.muted, margin: '0 0 7px 0', fontWeight: '800' }}>{lang === 'es' ? 'Nombre fiscal' : '开票名称'}</p>
                <input value={editData.invoiceName || ''} placeholder={editData.firstName || editData.lastName ? `${editData.firstName} ${editData.lastName}` : (lang === 'es' ? 'Nombre para factura' : '发票名称')} onChange={e => setEditData({ ...editData, invoiceName: e.target.value })} style={v2.field} />
              </div>
              <div>
                <p style={{ fontSize: '12px', color: v2.colors.muted, margin: '0 0 7px 0', fontWeight: '800' }}>RUC / Cédula</p>
                <input value={editData.invoiceRuc || ''} onChange={e => setEditData({ ...editData, invoiceRuc: e.target.value })} style={v2.field} />
              </div>
              <div>
                <p style={{ fontSize: '12px', color: v2.colors.muted, margin: '0 0 7px 0', fontWeight: '800' }}>DV</p>
                <input value={editData.invoiceDv || ''} onChange={e => setEditData({ ...editData, invoiceDv: e.target.value })} style={v2.field} />
              </div>
            </div>
          )}
        </div>
      )}

      {/* 收款银行 */}
      <div style={{ ...v2.card, padding: '16px', marginBottom: '14px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '12px' }}>
          <div style={{ width: '38px', height: '38px', borderRadius: '8px', background: v2.colors.softGold, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Wallet size={20} color={v2.colors.gold} />
          </div>
          <div>
            <p style={{ fontSize: '12px', color: v2.colors.muted, margin: '0 0 4px 0', fontWeight: '800' }}>{lang === 'es' ? 'Banco de retiro' : '收款银行'}</p>
            <h3 style={{ margin: 0, color: v2.colors.ink, fontSize: '17px' }}>{t.lbl_bank}</h3>
            <p style={{ margin: '6px 0 0 0', fontSize: '11px', color: '#854D0E', lineHeight: '1.4', fontWeight: '700' }}>{t.bank_notice}</p>
          </div>
        </div>

        <p style={{ fontSize: '12px', color: v2.colors.muted, margin: '0 0 7px 0', fontWeight: '800' }}>{t.lbl_acc_name}</p>
        <input disabled={isVerifiedWorker} value={editData.accName || ''} placeholder={t.lbl_acc_name} onChange={e => setEditData({ ...editData, accName: e.target.value })} style={{ ...fieldStyle(isVerifiedWorker), marginBottom: '10px' }} />
        {!isVerifiedWorker && editData.accName && !editBankNameMatches && (
          <div style={{ display: 'flex', gap: '7px', background: v2.colors.softRed, color: '#B91C1C', border: '1px solid #FECACA', borderRadius: '8px', padding: '9px', marginBottom: '10px' }}>
            <AlertTriangle size={15} style={{ flexShrink: 0 }} />
            <p style={{ margin: 0, fontSize: '11px', lineHeight: '1.35', fontWeight: '800' }}>{t.bank_match_err}</p>
          </div>
        )}

        <p style={{ fontSize: '12px', color: v2.colors.muted, margin: '0 0 7px 0', fontWeight: '800' }}>{t.lbl_acc_num}</p>
        <input disabled={isVerifiedWorker} value={editData.accNum || ''} placeholder={t.lbl_acc_num} onChange={e => setEditData({ ...editData, accNum: e.target.value })} style={{ ...fieldStyle(isVerifiedWorker), marginBottom: '10px' }} />
        <select disabled={isVerifiedWorker} value={editData.accType || 'ahorro'} onChange={e => setEditData({ ...editData, accType: e.target.value })} style={fieldStyle(isVerifiedWorker)}>
          <option value="ahorro">{lang === 'es' ? 'Cuenta de Ahorro' : '储蓄账户 (Ahorro)'}</option>
          <option value="corriente">{lang === 'es' ? 'Cuenta Corriente' : '支票账户 (Corriente)'}</option>
        </select>
        <div style={{ display: 'flex', gap: '7px', background: '#F8FAFC', color: '#475569', border: '1px solid #E3E8EF', borderRadius: '8px', padding: '10px', marginTop: '12px' }}>
          <Info size={15} style={{ flexShrink: 0 }} />
          <p style={{ margin: 0, fontSize: '11px', lineHeight: '1.4', fontWeight: '700' }}>{t.privacy_notice}</p>
        </div>
      </div>

      {/* 师傅：证件资料 */}
      {role === 'worker' && (
        <div style={{ ...v2.card, padding: '16px', marginBottom: '14px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', marginBottom: '12px' }}>
            <div>
              <p style={{ fontSize: '12px', color: v2.colors.muted, margin: '0 0 4px 0', fontWeight: '800' }}>{lang === 'es' ? 'Validación y contacto' : '审核与紧急联系'}</p>
              <h3 style={{ margin: 0, color: v2.colors.ink, fontSize: '17px' }}>{lang === 'es' ? 'Documentos oficiales' : '证件资料'}</h3>
            </div>
            {editDocsComplete ? <CheckCircle2 size={21} color={v2.colors.green} /> : <Clock size={21} color="#B45309" />}
          </div>
          <p style={{ fontSize: '12px', color: v2.colors.muted, margin: '0 0 7px 0', fontWeight: '800' }}>{t.lbl_ref}</p>
          <input value={editData.reference || ''} placeholder={lang === 'es' ? 'Nombre y celular...' : '姓名与电话...'} onChange={e => setEditData({ ...editData, reference: e.target.value })} style={{ ...v2.field, marginBottom: '14px' }} />
          <div style={{ display: 'flex', gap: '10px' }}>
            {renderPhotoBox('idPhoto', t.lbl_id)}
            {renderPhotoBox('selfie', t.lbl_selfie)}
          </div>
        </div>
      )}

      {/* 保存按钮 */}
      <button disabled={uploading} onClick={handleSaveProfile}
        style={{ width: '100%', padding: '16px', background: workerEditReady ? v2.colors.ink : '#CBD5E1', color: '#fff', borderRadius: '8px', marginTop: '2px', fontWeight: '900', cursor: uploading ? 'wait' : 'pointer', display: 'flex', justifyContent: 'center', alignItems: 'center', border: 'none', fontSize: '16px', boxShadow: workerEditReady ? '0 8px 18px rgba(15,23,42,0.14)' : 'none' }}>
        {uploading
          ? <Loader2 size={20} />
          : (role === 'worker' && !isVerifiedWorker
            ? (workerEditReady ? (lang === 'es' ? 'Guardar y enviar a revisión' : '保存并提交审核') : (lang === 'es' ? 'Completa los pasos pendientes' : '请先完成缺失资料'))
            : t.save)}
      </button>
    </div>
  );
}
